// <s-marquee> — seamless continuous scroll of its [data-marquee-track] content.
// Clones the track once for a gapless loop. Respects prefers-reduced-motion and pauses on hover.
//
// Default mode animates the track with the Web Animations API (not interactive).
// With `data-interactive`, the element itself is a native horizontal scroller that auto-scrolls:
// visitors can swipe, drag (mouse) or scroll it themselves, and it stops while pressed / held,
// hovered or focused, then resumes a moment after they let go. One component per file.
const RESUME_DELAY_MS = 1500;

class SMarquee extends HTMLElement {
  #animation = null;
  #ac = null;
  #mql = null;

  // Interactive mode state
  #raf = 0;
  #pos = 0;
  #lastSet = 0;
  #lastTime = 0;
  #distance = 0;
  #held = false;
  #hovered = false;
  #focused = false;
  #pauseUntil = 0;
  #drag = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const signal = this.#ac.signal;
    this.#mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.#mql.addEventListener("change", () => this.#setup(), { signal });

    if (this.hasAttribute("data-interactive")) {
      this.#bindInteractive(signal);
    } else {
      this.addEventListener("mouseenter", () => this.#animation?.pause(), { signal });
      this.addEventListener("mouseleave", () => this.#animation?.play(), { signal });
    }

    this.#setup();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#animation?.cancel();
    cancelAnimationFrame(this.#raf);
  }

  #ensureClones(track) {
    if (!track.dataset.cloned) {
      const clones = [...track.children].map((n) => {
        const clone = n.cloneNode(true);
        clone.setAttribute("aria-hidden", "true");
        clone.setAttribute("inert", "");
        return clone;
      });
      track.append(...clones);
      track.dataset.cloned = "true";
    }
  }

  #setup() {
    this.#animation?.cancel();
    this.#animation = null;
    cancelAnimationFrame(this.#raf);

    const track = this.querySelector("[data-marquee-track]");
    if (!track) return;

    if (this.hasAttribute("data-interactive")) {
      // Loop copies are needed for seamless manual scrolling too, even with reduced motion.
      this.#ensureClones(track);
      this.#distance = track.scrollWidth / 2;
      this.#pos = this.scrollLeft;
      this.#lastSet = this.scrollLeft;
      this.#lastTime = 0;
      if (!this.#mql.matches) this.#raf = requestAnimationFrame((t) => this.#tick(t));
      return;
    }

    if (this.#mql.matches) return;
    this.#ensureClones(track);

    const distance = track.scrollWidth / 2;
    if (distance <= 0) return;

    const speed = parseFloat(this.dataset.speed) || 40;
    const durationMs = (distance / speed) * 1000;
    const reverse = this.dataset.direction === "right";
    const from = reverse ? -distance : 0;
    const to = reverse ? 0 : -distance;

    this.#animation = track.animate(
      [{ transform: `translateX(${from}px)` }, { transform: `translateX(${to}px)` }],
      { duration: durationMs, iterations: Infinity, easing: "linear" },
    );
  }

  #bindInteractive(signal) {
    const pauseBriefly = () => {
      this.#pauseUntil = performance.now() + RESUME_DELAY_MS;
    };

    this.addEventListener("pointerenter", (e) => { if (e.pointerType === "mouse") this.#hovered = true; }, { signal });
    this.addEventListener("pointerleave", (e) => { if (e.pointerType === "mouse") this.#hovered = false; }, { signal });
    this.addEventListener("focusin", () => { this.#focused = true; }, { signal });
    this.addEventListener("focusout", () => { this.#focused = false; }, { signal });

    // Press & hold (touch or mouse) stops the band so it can be read calmly.
    this.addEventListener("pointerdown", (e) => {
      this.#held = true;
      if (e.pointerType === "mouse" && e.button === 0) {
        this.#drag = { x: e.clientX, start: this.scrollLeft, moved: false };
        this.setPointerCapture?.(e.pointerId);
      }
    }, { signal });

    this.addEventListener("pointermove", (e) => {
      if (!this.#drag) return;
      const dx = e.clientX - this.#drag.x;
      if (Math.abs(dx) > 3) this.#drag.moved = true;
      if (this.#drag.moved) {
        this.scrollLeft = this.#drag.start - dx;
        this.#wrap();
      }
    }, { signal });

    const release = () => {
      this.#held = false;
      this.#drag = null;
      pauseBriefly();
    };
    this.addEventListener("pointerup", release, { signal });
    this.addEventListener("pointercancel", release, { signal });
    this.addEventListener("touchend", release, { signal, passive: true });

    // A drag with the mouse must not trigger link clicks inside cards.
    this.addEventListener("click", (e) => {
      if (this.dataset.dragged === "true") {
        e.preventDefault();
        e.stopPropagation();
        delete this.dataset.dragged;
      }
    }, { signal, capture: true });
    this.addEventListener("pointerup", () => {
      if (this.#drag?.moved) this.dataset.dragged = "true";
    }, { signal, capture: true });

    // Native scrolling (swipe, trackpad, shift+wheel): follow it and keep the loop seamless.
    this.addEventListener("scroll", () => {
      if (Math.abs(this.scrollLeft - this.#lastSet) > 1) {
        pauseBriefly();
        this.#wrap();
        this.#pos = this.scrollLeft;
        this.#lastSet = this.scrollLeft;
      }
    }, { signal, passive: true });

    this.addEventListener("wheel", pauseBriefly, { signal, passive: true });
    window.addEventListener("resize", () => this.#setup(), { signal });
  }

  #wrap() {
    const d = this.#distance;
    if (d <= 0) return;
    if (this.scrollLeft >= d) this.scrollLeft -= d;
    else if (this.scrollLeft <= 0) this.scrollLeft += d;
    this.#pos = this.scrollLeft;
    this.#lastSet = this.scrollLeft;
  }

  #tick(time) {
    const dt = this.#lastTime ? Math.min(time - this.#lastTime, 100) : 0;
    this.#lastTime = time;

    const paused = this.#held || this.#hovered || this.#focused || time < this.#pauseUntil;
    if (!paused && this.#distance > 0) {
      const speed = parseFloat(this.dataset.speed) || 40;
      const dir = this.dataset.direction === "right" ? -1 : 1;
      this.#pos += dir * speed * (dt / 1000);
      if (this.#pos >= this.#distance) this.#pos -= this.#distance;
      if (this.#pos < 0) this.#pos += this.#distance;
      this.scrollLeft = this.#pos;
      this.#lastSet = this.scrollLeft;
    }

    this.#raf = requestAnimationFrame((t) => this.#tick(t));
  }
}

if (!customElements.get("s-marquee")) {
  customElements.define("s-marquee", SMarquee);
}
